import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Analytics from "@/components/Analytics";
import AttributionListener from "@/components/AttributionListener";
import ChatWidgetMount from "@/components/chat/ChatWidgetMount";
import CookieBanner from "@/components/CookieBanner";

// Public pages — eager (SSR + immediate paint on share)
import Home from "@/pages/Home";
import StoriesList from "@/pages/stories/StoriesList";
import StoryDetail from "@/pages/stories/StoryDetail";
import Nominate from "@/pages/Nominate";
import ForBrands from "@/pages/ForBrands";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Community from "@/pages/Community";
import Podcast from "@/pages/Podcast";
import PodcastDetail from "@/pages/PodcastDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Resources from "@/pages/Resources";
import ResourceDetail from "@/pages/ResourceDetail";
import AdminLogin from "@/pages/AdminLogin";

// Admin — lazy (heavy, behind auth, no SEO)
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminDashboardHome = lazy(() => import("@/pages/admin/AdminDashboardHome"));
const AdminStoriesList = lazy(() => import("@/pages/admin/AdminStoriesList"));
const AdminStoryForm = lazy(() => import("@/pages/admin/AdminStoryForm"));
const AdminSponsorsList = lazy(() => import("@/pages/admin/AdminSponsorsList"));
const AdminSponsorForm = lazy(() => import("@/pages/admin/AdminSponsorForm"));
const AdminEpisodesList = lazy(() => import("@/pages/admin/AdminEpisodesList"));
const AdminEpisodeForm = lazy(() => import("@/pages/admin/AdminEpisodeForm"));
const AdminEventsList = lazy(() => import("@/pages/admin/AdminEventsList"));
const AdminEventForm = lazy(() => import("@/pages/admin/AdminEventForm"));
const AdminArticlesList = lazy(() => import("@/pages/admin/AdminArticlesList"));
const AdminArticleForm = lazy(() => import("@/pages/admin/AdminArticleForm"));
const AdminSubscribers = lazy(() => import("@/pages/admin/AdminSubscribers"));
const AdminCampaigns = lazy(() => import("@/pages/admin/AdminCampaigns"));
const AdminCampaignEditor = lazy(() => import("@/pages/admin/AdminCampaignEditor"));
const AdminInstagram = lazy(() => import("@/pages/admin/AdminInstagram"));
const AdminMediaLibrary = lazy(() => import("@/pages/admin/AdminMediaLibrary"));
const AdminAIHistory = lazy(() => import("@/pages/admin/AdminAIHistory"));
const AdminAttribution = lazy(() => import("@/pages/admin/AdminAttribution"));
const AdminChat = lazy(() => import("@/pages/admin/AdminChat"));

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-cream text-charcoal/50">
    Cargando panel…
  </div>
);

// Phase 5I: helper to redirect parameterized aliases (e.g. /historias/:slug → /stories/:slug)
function NavigateWithSlug({ to }) {
  const { slug } = useParams();
  return <Navigate to={`${to}/${slug}`} replace />;
}

function App() {
  return (
    <>
      <Analytics />
      <AuthProvider>
        <BrowserRouter>
          <AttributionListener />
          <ChatWidgetMount />
          <CookieBanner />
          <Suspense fallback={<AdminFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              {/* Phase 5I: ES aliases (client-side replace; same SEO weight via canonical) */}
              <Route path="/historias" element={<Navigate to="/stories" replace />} />
              <Route path="/historias/:slug" element={<NavigateWithSlug to="/stories" />} />
              <Route path="/postula" element={<Navigate to="/nominate" replace />} />
              <Route path="/sponsors" element={<Navigate to="/for-brands" replace />} />
              <Route path="/sponsors/:slug" element={<NavigateWithSlug to="/for-brands" />} />
              <Route path="/marcas" element={<Navigate to="/for-brands" replace />} />
              <Route path="/para-marcas" element={<Navigate to="/for-brands" replace />} />
              <Route path="/eventos" element={<Navigate to="/events" replace />} />
              <Route path="/eventos/:slug" element={<NavigateWithSlug to="/events" />} />
              <Route path="/recursos" element={<Navigate to="/resources" replace />} />
              <Route path="/recursos/:slug" element={<NavigateWithSlug to="/resources" />} />
              <Route path="/stories" element={<StoriesList />} />
              <Route path="/stories/:slug" element={<StoryDetail />} />
              <Route path="/nominate" element={<Nominate />} />
              <Route path="/for-brands" element={<ForBrands />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/privacidad" element={<Navigate to="/privacy" replace />} />
              <Route path="/community" element={<Community />} />
              <Route path="/podcast" element={<Podcast />} />
              <Route path="/podcast/:slug" element={<PodcastDetail />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/:slug" element={<EventDetail />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/resources/:slug" element={<ResourceDetail />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboardHome /></ProtectedRoute>} />
              <Route path="/admin/submissions" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/stories" element={<ProtectedRoute><AdminStoriesList /></ProtectedRoute>} />
              <Route path="/admin/stories/:id" element={<ProtectedRoute><AdminStoryForm /></ProtectedRoute>} />
              <Route path="/admin/sponsors" element={<ProtectedRoute><AdminSponsorsList /></ProtectedRoute>} />
              <Route path="/admin/sponsors/:id" element={<ProtectedRoute><AdminSponsorForm /></ProtectedRoute>} />
              <Route path="/admin/episodes" element={<ProtectedRoute><AdminEpisodesList /></ProtectedRoute>} />
              <Route path="/admin/episodes/:id" element={<ProtectedRoute><AdminEpisodeForm /></ProtectedRoute>} />
              <Route path="/admin/events" element={<ProtectedRoute><AdminEventsList /></ProtectedRoute>} />
              <Route path="/admin/events/:id" element={<ProtectedRoute><AdminEventForm /></ProtectedRoute>} />
              <Route path="/admin/articles" element={<ProtectedRoute><AdminArticlesList /></ProtectedRoute>} />
              <Route path="/admin/articles/:id" element={<ProtectedRoute><AdminArticleForm /></ProtectedRoute>} />
              <Route path="/admin/subscribers" element={<ProtectedRoute><AdminSubscribers /></ProtectedRoute>} />
              <Route path="/admin/newsletter/campaigns" element={<ProtectedRoute><AdminCampaigns /></ProtectedRoute>} />
              <Route path="/admin/newsletter/campaigns/:id" element={<ProtectedRoute><AdminCampaignEditor /></ProtectedRoute>} />
              <Route path="/admin/instagram" element={<ProtectedRoute><AdminInstagram /></ProtectedRoute>} />
              <Route path="/admin/media" element={<ProtectedRoute><AdminMediaLibrary /></ProtectedRoute>} />
              <Route path="/admin/ai-history" element={<ProtectedRoute><AdminAIHistory /></ProtectedRoute>} />
              <Route path="/admin/attribution" element={<ProtectedRoute><AdminAttribution /></ProtectedRoute>} />
              <Route path="/admin/chat" element={<ProtectedRoute><AdminChat /></ProtectedRoute>} />
            </Routes>
          </Suspense>
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#1A1A1A",
                color: "#F5F0E8",
                border: "1px solid #C9A961",
                fontFamily: "Inter, sans-serif",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
